import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { CompanySettings } from '../../settings/model/company-settings';
import { ModulesService } from '../../shared/modules/modules.service';
import { ServerService } from '../../shared/server/server.service';
import { Version } from '../../shared/version/version';
import { DeviceManagementService } from '../device-management.service';
import { DeviceProfile } from '../model/device-profile.model';
import { moduleId } from '../model/module-id';
import { ApplicationSettings } from '../model/settings-definition/application-settings.model';
import { SettingsDefinition } from '../model/settings-definition/settings-definition.model';
import * as fromCompanySettingsStore from '../../settings/store';
import { Store } from '@ngrx/store';
import { TokenService } from '../../authentication/token.service';

@Injectable()
export class CfdService {
  public cfdProfiles$: Observable<Array<DeviceProfile>>;
  public allowToConfigureCfdSettings$: Observable<boolean>;
  private allowToConfigureCfdSettingsSubject: BehaviorSubject<
    boolean
  > = new BehaviorSubject(null);

  constructor(
    private deviceManagementService: DeviceManagementService,
    private modules: ModulesService,
    private server: ServerService,
    private tokenService: TokenService,
    private store: Store<fromCompanySettingsStore.CompanyState>
  ) {
    this.tokenService.tokenChanged$.subscribe((token: string) => {
      this.cfdProfiles$ = null;
      if (!!token) {
        this.allowedToConfigureCfdSettings();
      }
    });
    this.allowedToConfigureCfdSettings();
    this.allowToConfigureCfdSettings$ = this.allowToConfigureCfdSettingsSubject.asObservable();
  }

  public getCfdProfiles(
    forceReload: boolean = false
  ): Observable<Array<DeviceProfile>> {
    if (!this.cfdProfiles$ || forceReload) {
      this.cfdProfiles$ = this.deviceManagementService.getProfilesForApplication(
        forceReload,
        moduleId.CFD
      );
    }
    return this.cfdProfiles$;
  }

  public getCfdProfileById(id: any): Observable<DeviceProfile> {
    if (id === 'new' || Number(id) === NaN) {
      return fromCompanySettingsStore.getSettingsFromStore(this.store).pipe(
        map((company: CompanySettings) => {
          return new DeviceProfile(
            'Profile 1',
            company.id,
            true,
            moduleId.CFD,
            []
          );
        })
      );
    } else {
      return this.deviceManagementService.getProfileById(
        false,
        moduleId.CFD,
        +id
      );
    }
  }

  public getCfdApplicationSettings(): Observable<ApplicationSettings> {
    return this.deviceManagementService
      .getMostRecentSettingsDefinition(false, moduleId.RESTAURANT)
      .pipe(
        map((settingsDefinition: SettingsDefinition) => {
          return settingsDefinition ? settingsDefinition.cfd : null;
        })
      );
  }

  public allowedToConfigureCfdSettings(): void {
    combineLatest(
      this.getCfdApplicationSettings(),
      this.modules.isModuleEnabled(moduleId.CFD),
      this.server.hasAllowedServerVersion(new Version(5, 5, 0)),
      (
        hasApplicationSettings: boolean,
        cfdModuleEnabled: boolean,
        serverVersionMatched: boolean
      ) => {
        return (
          hasApplicationSettings != null &&
          cfdModuleEnabled &&
          serverVersionMatched
        );
      }
    ).subscribe((allow: boolean) => {
      this.allowToConfigureCfdSettingsSubject.next(allow);
    });
  }
}
